/* global debounce */

if (!customElements.get('predictive-search')) {
  class PredictiveSearch extends HTMLElement {
    constructor() {
      super();
      this.cachedResults = {};
      this.input = this.querySelector('.js-search-input');
      this.productTypeSelect = this.querySelector('.js-search-product-types');
      this.productTypeInput = document.getElementById('product_type_input');
      this.resetBtn = this.querySelector('.js-search-reset');
      this.results = this.querySelector('.js-search-results');
      this.overlay = this.querySelector('.js-search-overlay');
      this.statusEl = this.querySelector('.js-search-status');
      this.loadingText = this.getAttribute('data-loading-text');
      this.addListeners();
    }

    /**
     * Triggers when the web component is removed from the DOM
     */
    disconnectedCallback() {
      this.close();
    }

    addListeners() {
      this.input.addEventListener('focus', this.handleFocus.bind(this));
      // 28-10: felt der allerede har fokus (efter luk/Escape/tryk udenfor) udløser ikke 'focus' igen — åbn panelet ved tryk.
      this.input.addEventListener('click', () => { if (!this.hasAttribute('open')) this.handleFocus(); });
      this.input.addEventListener('input', debounce(this.handleInput.bind(this)));
      if (this.productTypeSelect) {
        this.productTypeSelect.addEventListener('change', this.handleProductTypeChange.bind(this));
      }
      this.querySelector('.search').addEventListener('submit', this.handleSubmit.bind(this));
      this.results.addEventListener('click', this.handleResultClick.bind(this));
    }

    /**
     * Gets the value of the search input field.
     * @returns {string}
     */
    getQuery() {
      return this.input.value.trim();
    }

    /**
     * Handles search form submit.
     * @param {object} evt - Event object.
     */
    handleSubmit(evt) {
      const vs = window.vejbSearch;
      if (vs && this._plan && this._plan.completed && this.getQuery().length && vs.norm(this._plan.q) !== vs.norm(this.getQuery())) {
        evt.preventDefault();
        vs.track('submit', { term: vs.norm(this.getQuery()), where: 'box', blended: vs.norm(this._plan.q) });
        const u = new URLSearchParams({ q: this._plan.q, vejb_from: this.getQuery(), type: 'product' });
        u.set('options[prefix]', 'last'); u.set('options[unavailable_products]', 'last');
        window.location.href = '/search?' + u.toString();
        return;
      }
      if (vs && this.getQuery().length) vs.track('submit', { term: vs.norm(this.getQuery()), where: 'box' });
      if (
        !this.getQuery().length
        || this.querySelector('.predictive-search__item[aria-selected="true"]:not(.js-submit)')
      ) {
        evt.preventDefault();
      }
    }

    /**
     * Handles 'input' events on the search field.
     */
    handleInput() {
      // autocomplete=off prevents browser from restoring value after using back button in Safari
      this.input.setAttribute('value', this.input.value);

      const searchTerm = this.getQuery();

      if (!searchTerm.length) {
        this.close();
        return;
      }

      this.getResults(searchTerm);
    }

    /**
     * Handles a change of product type
     * @param {object} evt - Event object.
     */
    handleProductTypeChange(evt) {
      this.productTypeInput.value = evt.detail.selectedValue;
      const query = this.getQuery();
      if (query.length > 0) {
        this.getResults(query);
      }
    }

    /**
     * Handles 'focus' events on the search field.
     */
    handleFocus() {
      if (window.vejbSearch) window.vejbSearch.loadVocab();
      const searchTerm = this.getQuery();
      if (!searchTerm.length) {
        // Tomt felt: åbn startpanelet (populære kategorier), så feltet tydeligt er aktivt.
        const startTpl = this.querySelector('.js-search-start');
        if (startTpl) {
          this.results.innerHTML = '';
          this.results.appendChild(startTpl.content.cloneNode(true));
          this.removeAttribute('results');
          this.fillChips();
          this.open();
          this.loadStartProducts();
        }
        return;
      }

      if (this.getAttribute('results') === 'true') {
        this.open();
      } else {
        this.getResults(searchTerm);
      }
    }

    /**
     * Populære søgninger (fra Clerk-data) som chips i tomt felt.
     */
    fillChips() {
      const D = window.VEJB_SEARCH || {};
      const box = this.querySelector('[data-search-chips]');
      if (!box || !D.popular || !D.popular.length) return;
      const esc = (t) => String(t).replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));
      const href = (w) => `/search?q=${encodeURIComponent(w)}&options%5Bprefix%5D=last&options%5Bunavailable_products%5D=last`;
      box.innerHTML = D.popular.map((w) => `<a class="search-start__chip js-vejb-chip" data-kind="popular" href="${href(w)}">${esc(w)}</a>`).join('');
      box.hidden = false;
    }

    /**
     * Fylder startpanelet med produkter: anbefalinger ud fra sidst sete produkt
     * (localStorage 'cc-recently-viewed'), ellers bestsellers.
     */
    async loadStartProducts() {
      const grid = this.querySelector('[data-search-start-products]');
      const titleEl = this.querySelector('[data-search-start-title]');
      if (!grid) return;
      const D = window.VEJB_SEARCH || {};

      if (!PredictiveSearch.startCache) {
        PredictiveSearch.startCache = (async () => {
          const html = async (url) => {
            const r = await fetch(url);
            if (!r.ok) return null;
            const tmpl = document.createElement('template');
            tmpl.innerHTML = await r.text();
            const ul = tmpl.content.querySelector('.search-start__grid');
            return ul && ul.children.length ? ul : null;
          };
          let recent = [];
          try { recent = JSON.parse(localStorage.getItem('cc-recently-viewed') || '[]'); } catch (e) { /* ignore */ }
          if (recent.length) {
            try {
              const ul = await html(`/recommendations/products?product_id=${recent[0]}&limit=8&intent=related&section_id=vejb-start-products`);
              if (ul && ul.children.length >= 4) return { title: (D.t && D.t.related) || '', markup: ul.innerHTML };
            } catch (e) { /* fall back to bestsellers */ }
          }
          const ul = await html('/collections/' + (D.bestCollection || 'mest-salda-i-lager') + '?section_id=vejb-start-products');
          return ul ? { title: (D.t && D.t.best) || '', markup: ul.innerHTML } : null;
        })();
      }

      try {
        const res = await PredictiveSearch.startCache;
        if (!grid.isConnected || !res) return;
        if (titleEl) titleEl.textContent = res.title;
        grid.innerHTML = res.markup;
      } catch (e) {
        if (grid.isConnected) grid.innerHTML = '';
      }
    }

    /**
     * Handles 'keydown' events on the custom element.
     * @param {object} evt - Event object.
     */
    handleKeydown(evt) {
      // Let tabs script handle keydown events on the tab buttons.
      if (evt.target.matches('.tablist__tab')) return;

      switch (evt.key) {
        case 'ArrowUp':
        case 'ArrowDown':
          evt.preventDefault();

          if (this.hasAttribute('open')) {
            this.handleResultsNav(evt.key);
          }
          break;

        case 'Enter':
          this.selectOption();
          break;

        case 'Escape':
          this.close();
          break;

        // no default
      }
    }

    /**
     * Handles 'keyup' events on the custom element.
     */
    handleKeyup() {
      // If search field is empty after key press, close the results.
      if (!this.getQuery().length) this.close();
    }

    /**
     * Handles keyboard navigation of the results.
     * @param {string} key - Key pressed.
     */
    handleResultsNav(key) {
      const selectedResult = this.querySelector(
        '[role="tabpanel"]:not([hidden]) [aria-selected="true"]'
      );
      const allResults = this.querySelectorAll(
        '[role="tabpanel"]:not([hidden]) .predictive-search__item'
      );
      let resultToSelect = allResults[0];

      if (key === 'ArrowUp') {
        if (!selectedResult) return;

        // Select the next result up or the last one if we've reached the top of the list.
        resultToSelect = selectedResult.previousElementSibling || allResults[allResults.length - 1];
      } else if (key === 'ArrowDown') {
        if (selectedResult) {
          // Select the next result down or the first one if we've reached the bottom of the list.
          resultToSelect = selectedResult.nextElementSibling || allResults[0];
        }
      }

      this.statusEl.textContent = '';

      if (resultToSelect) {
        // If the selected item didn't change, do nothing.
        if (resultToSelect === selectedResult) return;

        resultToSelect.setAttribute('aria-selected', 'true');
        if (selectedResult) selectedResult.setAttribute('aria-selected', 'false');

        this.setLiveRegionText(resultToSelect.textContent);
        this.input.setAttribute('aria-activedescendant', resultToSelect.id);
      }
    }

    /**
     * Selects a result
     */
    selectOption() {
      const selectedResult = this.querySelector('[aria-selected="true"] > .js-search-link');
      if (selectedResult) selectedResult.click();
    }

    /**
     * Gets the results for a search term.
     * @param {string} searchTerm - Search query.
     */
    async getResults(searchTerm, rescued, raw) {
      this.setLiveRegionLoadingState();

      this._plan = raw ? this._plan : null;
      this._typedNow = this.getQuery();
      if (window.vejbSearch && !raw) {
        searchTerm = window.vejbSearch.prepare(searchTerm);
        if (!rescued) {
          await Promise.race([window.vejbSearch.loadVocab(), new Promise((r) => setTimeout(r, 500))]);
          this._plan = window.vejbSearch.plan(searchTerm);
          searchTerm = this._plan.q;
        }
      }

      let searchFields = 'title,product_type,variants.title,vendor';
      if (theme.settings.pSearchIncludeSkus) searchFields += ',variants.sku';
      if (theme.settings.pSearchIncludeTags) searchFields += ',tag';

      let searchParams = '';
      if (this.productTypeInput && this.productTypeInput.value !== '') {
        searchParams = `q=product_type:${encodeURIComponent(this.productTypeInput.value)} AND ${encodeURIComponent(searchTerm)}`;
      } else {
        searchParams = `q=${encodeURIComponent(searchTerm)}`;
      }

      // Kun produkter + søgeforslag; udsolgte sidst; færre rækker på mobil.
      const lim = 12;
      searchParams += `&${encodeURIComponent('resources[type]')}=product,query`;
      searchParams += `&${encodeURIComponent('resources[options][unavailable_products]')}=last`;
      searchParams += `&${encodeURIComponent('resources[limit]')}=${lim}`;
      searchParams += `&${encodeURIComponent('resources[limit_scope]')}=${
        theme.settings.pSearchLimitScope
      }`;
      searchParams += `&${encodeURIComponent('resources[options][fields]')}=${searchFields}`;
      searchParams += '&section_id=predictive-search';

      const queryKey = searchParams.replace(' ', '-').toLowerCase();
      if (this.cachedResults[queryKey]) {
        this.renderResults(this.cachedResults[queryKey]);
        return;
      }

      try {
        const response = await fetch(`${theme.routes.predictiveSearch}?${searchParams}`);
        if (!response.ok) throw new Error(response.status);

        const tmpl = document.createElement('template');
        tmpl.innerHTML = await response.text();

        const resultsEl = tmpl.content.querySelector('#shopify-section-predictive-search');
        const resultsMarkup = resultsEl.innerHTML.replace(/psearch/g, this.input.id);

        // Få produkter af det gættede: bland tast-ordet ind som wildcard ("patro" → patro*), så vi ikke snævrer for meget ind.
        const found = resultsEl.querySelectorAll('.js-search-link[href*="/products/"]').length;
        if (!raw && this._plan && this._plan.completed && found > 0 && found < 4 && searchTerm.indexOf('*') < 0 && !this._plan.q.match(/\*/)) {
          const typedWord = this.getQuery().trim().split(' ').pop();
          if (typedWord.length >= 2 && this.getQuery() === this._typedNow) {
            const wide = searchTerm + ' OR ' + typedWord + '*';
            this._plan.q = wide;
            return this.getResults(wide, true, true);
          }
        }

        // Nul produkter (Shopify matcher ikke ufuldstændige ord): prøv nærmeste forslag med træf.
        if (!rescued && window.vejbSearch && !resultsEl.querySelector('.js-search-link[href*="/products/"]')) {
          const typed = this.getQuery();
          const alt = await window.vejbSearch.rescue(searchTerm);
          if (alt && this.getQuery() === typed) {
            window.vejbSearch.track('rescue', { term: window.vejbSearch.norm(typed), alt: window.vejbSearch.norm(alt), where: 'dropdown' });
            return this.getResults(alt, true);
          }
        }

        this.cachedResults[queryKey] = resultsMarkup;
        this.renderResults(resultsMarkup);
      } catch (error) {
        this.close();
        throw error;
      }
    }

    /**
     * Sets the live region loading state.
     */
    setLiveRegionLoadingState() {
      this.setLiveRegionText(this.loadingText);
    }

    /**
     * Sets the live region text.
     * @param {string} statusText - Status text.
     */
    setLiveRegionText(statusText) {
      this.statusEl.setAttribute('aria-hidden', 'false');
      this.statusEl.textContent = statusText;

      setTimeout(() => {
        this.statusEl.setAttribute('aria-hidden', 'true');
      }, 1000);
    }

    /**
     * Egne søgeforslag fra Clerk-søgedata (populære søgeord der matcher det kunden taster).
     */
    addSuggestions() {
      const root = this.results.querySelector('.predictive-search__results');
      const vs = window.vejbSearch;
      const pl = this._plan;
      if (!root || !vs) return;
      const esc = (t) => String(t).replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));
      const words = [];
      if (pl) (pl.completed ? pl.words : pl.alts).forEach((w) => words.push(w));
      // det der faktisk blev tastet, som sidste valg, når vi har gættet et andet ord
      if (pl && pl.completed && vs.norm(this.getQuery()) !== vs.norm(pl.q) && words.indexOf(this.getQuery().trim()) < 0) words.push(this.getQuery().trim());
      if (!words.length) return;
      // vores ordgæt erstatter Shopifys lodrette forslagsliste
      const sh = root.querySelector('.predictive-search__suggestions');
      if (sh) sh.remove();
      if (root.querySelector('.vejb-suggest')) return;
      const wrap = document.createElement('div');
      wrap.className = 'vejb-suggest';
      wrap.innerHTML = words.map((w, i) => `<button type="button" class="vejb-suggest__item js-vejb-fill" data-q="${esc(w)}">${esc(w)}</button>`).join('');
      root.insertBefore(wrap, root.firstChild);
    }

    /**
     * Renders the results markup.
     * @param {string} resultsMarkup - Results markup.
     */
    renderResults(resultsMarkup) {
      this.results.innerHTML = resultsMarkup;
      this.addSuggestions();
      this.afterRender();
      this.setAttribute('results', true);
      this.open();
    }

    /**
     * Måling + nul-resultat-fallback (populære søgninger i stedet for en tom boks).
     */
    afterRender() {
      const vs = window.vejbSearch;
      if (!vs) return;
      const term = vs.norm(this.getQuery());
      const n = this.results.querySelectorAll('.predictive-search__tabpanel .js-search-link[href*="/products/"]').length;
      clearTimeout(this._tt);
      this._tt = setTimeout(() => {
        if (term !== vs.norm(this.getQuery())) return;
        vs.track(n ? 'dropdown' : 'zero', { term, results: n, where: 'dropdown' });
      }, 900);
      if (!n) {
        const root = this.results.querySelector('.predictive-search__results');
        const D = window.VEJB_SEARCH || {};
        if (root && D.popular && D.popular.length && !root.querySelector('.vejb-fallback')) {
          const esc = (t) => String(t).replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));
          const wrap = document.createElement('div');
          wrap.className = 'vejb-suggest vejb-fallback';
          wrap.innerHTML = ((D.t && D.t.popular) ? `<span class="search-start__chips-title">${esc(D.t.popular)}</span>` : '')
            + D.popular.slice(0, 6).map((w) => `<a class="vejb-suggest__item js-vejb-chip" data-kind="fallback" href="/search?q=${encodeURIComponent(w)}&options%5Bprefix%5D=last&options%5Bunavailable_products%5D=last">${esc(w)}</a>`).join('');
          root.appendChild(wrap);
        }
      }
    }

    /**
     * Klik-måling og "ryd seneste søgninger" (delegeret, så det virker på nyt indhold).
     */
    handleResultClick(evt) {
      const vs = window.vejbSearch;
      if (!vs) return;
      const fill = evt.target.closest && evt.target.closest('.js-vejb-fill');
      if (fill) {
        evt.preventDefault();
        this.input.value = fill.dataset.q;
        this.input.setAttribute('value', fill.dataset.q);
        vs.track('chip', { term: vs.norm(fill.dataset.q), kind: 'ordgaet', typed: vs.norm(this.getQuery()) });
        this.getResults(fill.dataset.q, true);
        return;
      }
      const term = vs.norm(this.getQuery());
      const chip = evt.target.closest && evt.target.closest('.js-vejb-chip, .js-vejb-suggest');
      if (chip) {
        vs.track('chip', { term: vs.norm(chip.textContent), kind: chip.dataset.kind || 'suggest', typed: term });
        return;
      }
      const a = evt.target.closest && evt.target.closest('.predictive-search__tabpanel a.js-search-link');
      if (a) {
        const all = Array.prototype.slice.call(this.results.querySelectorAll('.predictive-search__tabpanel a.js-search-link[href*="/products/"]'));
        vs.track('click', { term, where: 'dropdown', position: all.indexOf(a) + 1, handle: (a.getAttribute('href').split('/products/')[1] || '').split('?')[0] });
        return;
      }
      const sug = evt.target.closest && evt.target.closest('.predictive-result--suggestion');
      if (sug) { vs.track('chip', { term: vs.norm(sug.textContent), kind: 'shopify_suggestion', typed: term }); }
    }

    /**
     * Panelhøjden følger den synlige skærm (tastaturet åbent på mobil), så produkterne ikke skjules under kanten.
     */
    followViewport() {
      const vv = window.visualViewport;
      if (!vv || this._vv) return;
      this._vv = () => document.documentElement.style.setProperty('--vejb-vvh', vv.height + 'px');
      vv.addEventListener('resize', this._vv);
      this._vv();
    }

    /**
     * Opens the results listbox.
     */
    open() {
      this.followViewport();
      this.overlay.classList.add('is-visible');
      if (this.getQuery().length) this.resetBtn.hidden = false;
      this.input.setAttribute('aria-expanded', 'true');
      this.setAttribute('open', '');
      document.body.classList.add('overlay-predictive-search');

      // Add event handlers (so the bound event listeners can be removed).
      this.keydownHandler = this.keydownHandler || this.handleKeydown.bind(this);
      this.keyupHandler = this.keyupHandler || this.handleKeyup.bind(this);
      this.resetBtnClickHandler = this.resetBtnClickHandler || this.close.bind(this);
      this.overlayClickHandler = this.overlayClickHandler || this.close.bind(this);

      // Add event listeners (for while results are open).
      this.addEventListener('keydown', this.keydownHandler);
      this.addEventListener('keyup', this.keyupHandler);
      this.resetBtn.addEventListener('click', this.resetBtnClickHandler);
      this.overlay.addEventListener('click', this.overlayClickHandler);
    }

    /**
     * Closes the results listbox.
     * @param {object} evt - Event object.
     */
    close(evt) {
      // If reset button was clicked, empty the input field.
      if (evt && evt.target === this.resetBtn) {
        this.input.value = '';
        this.removeAttribute('results');
        this.input.focus();
      }

      // Deselect the selected result (if there is one).
      const selected = this.querySelector('.predictive-search__item[aria-selected="true"]');
      if (selected) selected.setAttribute('aria-selected', 'false');

      this.removeAttribute('open');
      this.resetBtn.hidden = true;
      this.overlay.classList.remove('is-visible');
      this.input.setAttribute('aria-activedescendant', '');
      this.input.setAttribute('aria-expanded', 'false');
      document.body.classList.remove('overlay-predictive-search');

      // Remove event listeners added on search results opening.
      this.removeEventListener('keydown', this.keydownHandler);
      this.resetBtn.removeEventListener('click', this.resetBtnClickHandler);
      this.overlay.removeEventListener('click', this.overlayClickHandler);
    }
  }

  customElements.define('predictive-search', PredictiveSearch);
}
